// ═══ 평가 정산 페이지 — EvalAdminPage.js에서 분리 ═══
// 월별 검차원 정산: 검차 건수/기본료/등급 보너스 합계 + 정산 처리
// 2026-10-29 — 화면을 v2 키트(히어로·카드·장부·월 이동)로 '업체 정산'(v2/eval/parts/UsageCompanies)과 맞춤.
//   처리 로직(조회·재집계 미리보기·정산 생성/완료·PDF)은 그대로.
// 2026-10-30 Jin — '정산 생성'을 눌러야만 목록에 보이던 것을 없앤다: 월을 열 때 미리보기가 저장된
//   정산과 다르면 미정산 정산을 자동으로 최신화(generate, settled 는 서버가 건드리지 않음).
//   정산완료 뒤 늘어난 검차는 '정산 후 추가 N건' 배지로 보인다 (지급분은 불변 — 다음 달 이월 판단은 사람).
import React, { useState, useEffect, useRef } from 'react';
import { authFetch } from '../utils/api';
import { useIsMobile } from '../utils/useMobile';
import { T, S, FONT, NUM, R } from '../v2/theme';
import { HeroStrip, Card, Ledger, EmptyState, PeriodNav, Pill } from '../v2/kit';
import { LevelBadge } from './CommonUI';
import PageBar from '../v2/eval/parts/PageBar';
import CustomModal from './CustomModal';
import useDetailHistory from '../v2/eval/hooks/useDetailHistory';

function won(n) { return (Number(n) || 0).toLocaleString(); }
function ymOf(y, m) { return y + '-' + (m < 10 ? '0' + m : '' + m); }
var sub = { fontSize: FONT.label, color: T.faint };
var warnBox = { background: T.warnTint, border: '1px solid ' + T.warnBd, borderRadius: R.card, padding: '10px 14px', fontSize: 12.5, color: T.warn, marginBottom: 14 };
function StatusTag(props) { return props.settled ? <Pill tone="ok">정산완료</Pill> : <Pill tone="warn">미정산</Pill>; }

// ym/onYm/barExtra — v2 '평가사 정산' 탭(InspectorSettleTab)이 작업 내역과 월을 공유하고 보기 전환을 제목 줄에 넣는다 (2026-10-30).
function SettlementPage({ BASE, onBack, ym, onYm, barExtra }) {
  var isMobile = useIsMobile();
  var now = new Date();
  var [year, setYear] = useState(ym ? Number(ym.slice(0, 4)) : now.getFullYear());
  var [month, setMonth] = useState(ym ? Number(ym.slice(5, 7)) : now.getMonth() + 1);
  var [settlements, setSettlements] = useState([]);
  // R4 (2026-08-31) — generate dry-run(preview) 결과. 배지/재집계 요약 전용, DB 미변경.
  var [preview, setPreview] = useState([]);
  var [selectedId, setSelectedId] = useState(null);
  // 브라우저 뒤로가기 = 상세 → 목록 (2026-10-30). setDetail 은 아래 선언 — 호출 시점엔 정의돼 있다.
  var backToList = useDetailHistory('settlement', !!selectedId, function() { setSelectedId(null); setDetail(null); });
  var [detail, setDetail] = useState(null);
  var [loading, setLoading] = useState(false);
  // Wave S3 (2026-05-21) — 글로벌 mutation 로딩 상태 (generate / settle). 중복 클릭 차단.
  var [submitting, setSubmitting] = useState(false);
  // Wave S1 (2026-05-21) — 통합 에러/성공 모달. silent catch + native alert 일괄 전환.
  var [modal, setModal] = useState(null);
  var closeModal = function() { setModal(null); };
  var showError = function(err, defaultMsg) {
    var msg = (err && err.response && err.response.data && err.response.data.error) ||
              (err && err.message) ||
              (typeof err === 'string' ? err : null) ||
              defaultMsg || '오류가 발생했습니다';
    setModal({ type: 'danger', title: '실패', message: msg, hideCancel: true, onConfirm: closeModal });
  };

  // ═══ R4 (2026-08-31) — 집계 신선도 ═══
  // 실사고: 평가사가 "7월에 10건 넘게 했는데 정산엔 5건" 신고. 정산은 관리자가 눌러야 찍히는
  //   스냅샷인데 화면에 "이 숫자가 언제 집계된 것인지" 표기가 전혀 없어 오해가 증폭됐다.
  //   서버는 created_at(최초 집계) / updated_at(재집계) 를 이미 내려준다 (routes SELECT s.*).
  // updated_at 우선, 없으면 created_at. 둘 다 없으면 표기 생략 — 없는 값을 만들어내지 않는다.
  var pickAggAt = function(row) { return (row && (row.updated_at || row.created_at)) || ''; };
  // 'YYYY-MM-DD HH:mm:ss'(sqlite) 와 ISO Z(mysql2 Date → JSON) 두 형태 모두 수용.
  //   ISO 는 +9h 후 UTC 필드로 KST wall clock 복원 — utils/pdfTemplates.js fmtDate10 과 동일 규약.
  var fmtStamp = function(v) {
    if (!v) return '';
    var str = String(v);
    if (str.indexOf('T') > -1) {
      var d = new Date(str);
      if (isNaN(d.getTime())) return '';
      var k = new Date(d.getTime() + 9 * 3600 * 1000);
      return (k.getUTCMonth() + 1) + '/' + k.getUTCDate() + ' ' + String(k.getUTCHours()).padStart(2, '0') + ':' + String(k.getUTCMinutes()).padStart(2, '0');
    }
    var m = str.match(/^(\d{4})-(\d{2})-(\d{2})[ ](\d{2}):(\d{2})/);
    if (!m) return '';
    return Number(m[2]) + '/' + Number(m[3]) + ' ' + m[4] + ':' + m[5];
  };
  var stampMs = function(v) {
    if (!v) return 0;
    var t = new Date(String(v).replace(' ', 'T')).getTime();
    return isNaN(t) ? 0 : t;
  };
  // preview 응답 매칭 키는 평가사 이름 — 서버 계약이 { inspector, level, jobs, total, action, before } 로
  //   id 를 주지 않는다.
  var previewFor = function(name) {
    for (var i = 0; i < preview.length; i++) { if (preview[i].inspector === name) return preview[i]; }
    return null;
  };
  var badgeFor = function(name) {
    var p = previewFor(name);
    if (!p) return '';
    if (p.action === 'delete') return '정리 예정';
    if (p.action === 'skip-settled' && p.before && p.jobs > p.before.jobs) return '정산 후 추가 ' + (p.jobs - p.before.jobs) + '건';
    if (p.action === 'update' && p.before && p.jobs !== p.before.jobs) {
      var diff = p.jobs - p.before.jobs;
      return diff > 0 ? '미반영 ' + diff + '건' : '감소 ' + Math.abs(diff) + '건';
    }
    return '';
  };
  // 재집계 예상 요약 — 상단 배너 + '정산 생성' 확인 모달 공용.
  var previewNotes = function() {
    var unreflected = 0, creates = 0, createJobs = 0, deletes = 0;
    for (var i = 0; i < preview.length; i++) {
      var p = preview[i];
      if (p.action === 'create') { creates += 1; createJobs += (p.jobs || 0); }
      else if (p.action === 'delete') { deletes += 1; }
      else if (p.action === 'update' && p.before && p.jobs !== p.before.jobs) { unreflected += (p.jobs - p.before.jobs); }
    }
    var parts = [];
    if (unreflected > 0) parts.push('미반영 ' + unreflected + '건');
    if (unreflected < 0) parts.push('감소 ' + Math.abs(unreflected) + '건');
    if (creates > 0) parts.push('신규 평가사 ' + creates + '명 (' + createJobs + '건)');
    if (deletes > 0) parts.push('정리 예정 ' + deletes + '건');
    return parts;
  };

  var fetchSettlements = function() {
    authFetch(BASE + '/api/eval/settlements?year=' + year + '&month=' + month).then(function(r) { return r.json(); }).then(function(d) {
      if (d.success) setSettlements(d.data || []);
    }).catch(function(e) { showError(e); });
  };

  // 상세 로딩
  useEffect(function() {
    if (!selectedId) { setDetail(null); return; }
    authFetch(BASE + '/api/eval/settlements/' + selectedId).then(function(r) { return r.json(); }).then(function(d) {
      if (d.success) setDetail(d.data);
      else { setDetail(null); setSelectedId(null); }
    }).catch(function(e) { setDetail(null); setSelectedId(null); showError(e); });
  }, [selectedId]);

  // preview(dry-run) — DB 미변경이라 월 진입 시 안전하게 호출. 실패해도 화면은 정상 동작(배지만 생략).
  var fetchPreview = function() {
    authFetch(BASE + '/api/eval/settlements/generate', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ year: year, month: month, preview: true }),
    }).then(function(r) { return r.json(); }).then(function(d) {
      setPreview((d && d.success && d.preview && Array.isArray(d.data)) ? d.data : []);
    }).catch(function() { setPreview([]); });
  };

  useEffect(function() { fetchSettlements(); fetchPreview(); }, [year, month]);

  // 자동 최신화 (2026-10-30 Jin) — 미리보기가 저장된 미정산 정산과 다르면 조용히 generate 한 번.
  //   같은 달은 화면에 머무는 동안 한 번만 시도(실패·경합 시 무한 반복 방지). 실패하면 '재집계 필요' 안내가 남는다.
  var autoSynced = useRef('');
  var needsSync = function(list) {
    return list.some(function(p) {
      if (p.action === 'create' || p.action === 'delete') return true;
      return p.action === 'update' && p.before && (p.jobs !== p.before.jobs || p.total !== p.before.total);
    });
  };
  useEffect(function() {
    var key = year + '-' + month;
    if (!preview.length || autoSynced.current === key || !needsSync(preview)) return;
    autoSynced.current = key;
    authFetch(BASE + '/api/eval/settlements/generate', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ year: year, month: month }),
    }).then(function(r) { return r.json(); }).then(function(d) {
      if (d && d.success) { fetchSettlements(); fetchPreview(); }
    }).catch(function() { /* 안내 배너가 남아 수동 '정산 생성' 으로 처리 가능 */ });
  }, [preview]); // eslint-disable-line react-hooks/exhaustive-deps

  // R4 (2026-08-31) — '정산 생성' 확인 모달. handleSettle(Wave S2) 패턴 복제.
  //   기존엔 확인 없이 즉시 POST — 재집계는 미정산 스냅샷을 지금 값으로 덮어쓴다.
  var handleGenerate = function() {
    var parts = previewNotes();
    var summary = parts.length
      ? '예상 변경: ' + parts.join(' · ')
      : '변경 예상 내역을 불러오지 못했습니다 (집계 후 값이 달라질 수 있습니다).';
    setModal({
      type: 'confirm',
      title: '정산 생성',
      message: year + '년 ' + month + '월 정산을 다시 집계합니다.\n' + summary + '\n미정산 스냅샷은 지금 값으로 갱신되고, 정산완료 건은 변경되지 않습니다.\n계속하시겠습니까?',
      confirmText: '정산 생성',
      onConfirm: function() {
        closeModal();
        doGenerate();
      },
    });
  };
  var doGenerate = function() {
    setLoading(true);
    setSubmitting(true);
    authFetch(BASE + '/api/eval/settlements/generate', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ year: year, month: month }),
    }).then(function(r) { return r.json(); }).then(function(d) {
      if (d && d.success) {
        var n = (d.data || []).length;
        var total = (d.data || []).reduce(function(s, x) { return s + (x.total || 0); }, 0);
        var msg = '정산 ' + n + '건 ' + (n > 0 ? '생성/갱신 완료 (합계 ' + total.toLocaleString() + '원)' : '대상 없음 — 해당 월에 완료된 검차가 없습니다.');
        setModal({ type: 'success', title: '정산 생성', message: msg, onConfirm: closeModal });
        fetchSettlements();
        fetchPreview();
      } else {
        showError(d && d.error, '정산 생성 실패');
      }
    }).catch(function(err) {
      showError(err, '정산 생성 네트워크 오류');
    }).finally(function() { setLoading(false); setSubmitting(false); });
  };

  // Wave S2 (2026-05-21) — 정산 완료 처리 전 확인 다이얼로그. 되돌릴 수 없는 결정.
  var handleSettle = function(id) {
    setModal({
      type: 'confirm',
      title: '정산 완료 처리',
      message: '이 정산을 완료 처리합니다. 처리 후 status가 settled로 영구 변경되며 되돌릴 수 없습니다.\n계속하시겠습니까?',
      confirmText: '정산 완료',
      onConfirm: function() {
        closeModal();
        doSettle(id);
      },
    });
  };
  var doSettle = function(id) {
    setSubmitting(true);
    authFetch(BASE + '/api/eval/settlements/' + id + '/settle', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
    }).then(function() { fetchSettlements(); setSelectedId(null); })
      .catch(function(e) { showError(e, '정산 완료 처리 실패'); })
      .finally(function() { setSubmitting(false); });
  };

  // Wave S4 (2026-05-21) — 정산서 PDF 다운로드. 토큰 발급 후 <a href> native 다운로드.
  //   <a href> 는 Authorization 헤더 못 실음 → 짧은 TTL 서명 토큰 + ?token= 쿼리.
  var handleDownloadPdf = function(id) {
    setSubmitting(true);
    authFetch(BASE + '/api/eval/settlements/' + id + '/download-token', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
    }).then(function(r) { return r.json(); }).then(function(d) {
      if (d && d.success && d.token) {
        var url = BASE + '/api/eval/settlements/' + id + '/pdf?token=' + encodeURIComponent(d.token);
        var a = document.createElement('a');
        a.href = url;
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        showError(d && d.error, 'PDF 다운로드 토큰 발급 실패');
      }
    }).catch(function(e) {
      showError(e, 'PDF 다운로드 토큰 발급 실패');
    }).finally(function() { setSubmitting(false); });
  };

  // Wave S3 (2026-05-21) — 글로벌 mutation 오버레이. submitting 시 클릭 차단 + 시각 피드백.
  var overlay = submitting ? (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15,23,42,0.45)', zIndex: 9998, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'auto' }}>
      <div style={{ background: T.card, borderRadius: R.lg, padding: '20px 32px', display: 'flex', alignItems: 'center', gap: 12, boxShadow: T.shadowMenu }}>
        <div className="spinner" style={{ width: 22, height: 22, borderRadius: '50%', border: '3px solid ' + T.bd, borderTopColor: T.navy, animation: 'spin 0.8s linear infinite' }}/>
        <span style={{ fontSize: 14, fontWeight: 600, color: T.navy }}>처리 중...</span>
      </div>
      <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
    </div>
  ) : null;

  var back = { label: '‹ 정산 목록', onClick: backToList };

  // 상세 로딩 중
  if (selectedId && !detail) {
    return (<div>
      {overlay}
      <PageBar back={back} title="평가사 정산 상세"/>
      <Card><EmptyState loaded={false}/></Card>
      {modal && <CustomModal isOpen={true} {...modal} onCancel={closeModal} />}
    </div>);
  }

  // 상세 보기
  if (selectedId && detail) {
    var items = detail.items || [];
    var baseSum = items.reduce(function(s, it) { return s + (Number(it.base_fee) || 0); }, 0);
    var hasCompany = items.some(function(it) { return it.company_name !== undefined; });
    return (<div>
      {overlay}
      <PageBar back={back}
        title={<React.Fragment>{detail.inspector_name} <LevelBadge level={detail.inspector_level}/> <StatusTag settled={detail.status === 'settled'}/></React.Fragment>}
        right={<React.Fragment>
          {/* Wave S4 (2026-05-21) — 정산서 PDF 다운로드 (상태 무관). 평가사 본인 전달용. */}
          <button type="button" style={Object.assign({}, S.btn, S.btnSm)} disabled={submitting} onClick={function() { handleDownloadPdf(detail.id); }}>정산서 PDF</button>
          {detail.status !== 'settled' && <button type="button" style={Object.assign({}, S.btnNavy, S.btnSm)} onClick={function() { handleSettle(detail.id); }}>정산 완료 처리</button>}
        </React.Fragment>}/>
      <HeroStrip isMobile={isMobile} metrics={[
        { key: 'jobs', label: '검차 건수', value: detail.total_jobs || 0, unit: '건' },
        { key: 'base', label: '기본 검차비', value: won(baseSum), unit: '원' },
        { key: 'bonus', label: '등급 보너스', value: won(detail.level_bonus), unit: '원', tone: detail.level_bonus ? 'default' : 'faint' },
        { key: 'total', label: '지급 합계', value: won(detail.total_amount), unit: '원', tone: 'money', sub: fmtStamp(pickAggAt(detail)) ? '최종 집계 ' + fmtStamp(pickAggAt(detail)) : undefined },
      ]}/>
      <Card>
        {!items.length ? <EmptyState loaded text="검차 내역이 없습니다"/> : (
          <Ledger minWidth={hasCompany ? 820 : 680} foot={<React.Fragment>
            <span>검차 <b style={NUM}>{items.length}</b>건</span><span className="mute">금액 단위 원 · 기본 검차비 + 등급 보너스</span>
          </React.Fragment>}>
            <thead><tr>
              <th style={{ width: 110 }}>검차일</th><th style={{ width: 130 }}>평가 번호</th><th>차량</th>
              {/* 요청 업체 — 서버가 관리자에게만 company_name 을 싣는다 (2026-10-29 Jin). 업체 미지정 초기 오더는 '-' */}
              {hasCompany && <th style={{ width: 150 }}>요청 업체</th>}
              <th className="num" style={{ width: 100 }}>기본</th><th className="num" style={{ width: 90 }}>보너스</th><th className="num" style={{ width: 110 }}>금액</th>
            </tr></thead>
            <tbody>
              {items.map(function(it, i) {
                return (<tr key={i}>
                  <td style={NUM}>{it.inspect_date || '-'}</td>
                  <td style={NUM}>{it.order_no}</td>
                  <td><span className="k">{it.car_model}</span>{it.car_number ? <span style={Object.assign({ marginLeft: 6 }, sub)}>{it.car_number}</span> : null}</td>
                  {hasCompany && <td>{it.company_name || <span className="mute">-</span>}</td>}
                  <td className="num">{won(it.base_fee)}</td>
                  <td className="num">{it.level_bonus ? won(it.level_bonus) : <span className="mute">-</span>}</td>
                  <td className="num money">{won(it.total_fee)}</td>
                </tr>);
              })}
            </tbody>
          </Ledger>
        )}
      </Card>
      {modal && <CustomModal isOpen={true} {...modal} onCancel={closeModal} />}
    </div>);
  }

  var totalAmount = settlements.reduce(function(s, x) { return s + (x.total_amount || 0); }, 0);
  var totalJobs = settlements.reduce(function(s, x) { return s + (x.total_jobs || 0); }, 0);
  var lastAggAt = settlements.reduce(function(acc, x) {
    var v = pickAggAt(x);
    return stampMs(v) > stampMs(acc) ? v : acc;
  }, '');
  var previewParts = previewNotes();
  var unsettled = settlements.filter(function(x) { return x.status !== 'settled'; });

  return (<div>
    {overlay}
    {/* onBack — 탭이 없는 v1 EvalAdminPage 전용. v2 EvalScreen 은 탭으로 이동하므로 넘기지 않는다. */}
    <PageBar back={onBack ? { label: '‹ 평가 오더', onClick: onBack } : null} title="평가사 정산" right={<React.Fragment>
      {barExtra || null}
      <PeriodNav value={ymOf(year, month)} onChange={function(v) { var p = v.split('-'); setYear(Number(p[0])); setMonth(Number(p[1])); if (onYm) onYm(v); }}/>
      <button type="button" style={Object.assign({}, S.btnPrimary, S.btnSm)} disabled={loading} onClick={handleGenerate}>{loading ? '생성중...' : '정산 생성'}</button>
    </React.Fragment>}/>
    <HeroStrip isMobile={isMobile} metrics={[
      { key: 'ins', label: '정산 평가사', value: settlements.length, unit: '명' },
      { key: 'jobs', label: '총 검차', value: totalJobs, unit: '건' },
      { key: 'total', label: '지급 합계', value: won(totalAmount), unit: '원', tone: 'money', sub: fmtStamp(lastAggAt) ? '최종 집계 ' + fmtStamp(lastAggAt) : undefined },
      { key: 'unsettled', label: '미정산', value: won(unsettled.reduce(function(s, x) { return s + (x.total_amount || 0); }, 0)), unit: '원',
        tone: unsettled.length ? 'danger' : 'faint', sub: unsettled.length ? unsettled.length + '명' : undefined },
    ]}/>
    {/* 자동 최신화를 시도한 뒤에도 차이가 남을 때만 (그 전엔 곧 사라질 안내라 깜빡임만 생긴다) */}
    {previewParts.length > 0 && autoSynced.current === year + '-' + month && (
      <div style={warnBox}>
        <b>재집계 필요</b> — {previewParts.join(' · ')}
        <span style={{ marginLeft: 8, opacity: 0.85 }}>"정산 생성"을 눌러야 반영됩니다.</span>
      </div>
    )}
    <Card>
      {settlements.length === 0 ? <EmptyState loaded text='정산 데이터가 없습니다. "정산 생성"을 눌러주세요.'/> : (
        <Ledger minWidth={680} foot={<React.Fragment>
          <span>평가사 <b style={NUM}>{settlements.length}</b>명</span><span className="mute">금액 단위 원 · 행을 누르면 검차 내역</span>
        </React.Fragment>}>
          <thead><tr>
            <th>평가사</th><th style={{ width: 100 }}>상태</th><th className="num" style={{ width: 80 }}>건수</th>
            <th className="num" style={{ width: 110 }}>기본</th><th className="num" style={{ width: 100 }}>보너스</th><th className="num" style={{ width: 120 }}>합계</th>
          </tr></thead>
          <tbody>
            {settlements.map(function(s) {
              var badge = badgeFor(s.inspector_name);
              return (<tr key={s.id} onClick={function() { setSelectedId(s.id); }} style={{ cursor: 'pointer' }}>
                <td><span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span className="k">{s.inspector_name}</span><LevelBadge level={s.inspector_level}/>
                  {badge && <Pill tone="warn">{badge}</Pill>}
                </span></td>
                <td><StatusTag settled={s.status === 'settled'}/></td>
                <td className="num" style={NUM}>{s.total_jobs}</td>
                <td className="num">{won(s.base_fee)}</td>
                <td className="num">{s.level_bonus ? won(s.level_bonus) : <span className="mute">-</span>}</td>
                <td className="num money">{won(s.total_amount)}</td>
              </tr>);
            })}
          </tbody>
        </Ledger>
      )}
    </Card>
    {modal && <CustomModal isOpen={true} {...modal} onCancel={closeModal} />}
  </div>);
}

export default SettlementPage;
